import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { toUtcDay } from "components/shared/TitlesTerms/TableColumnsFormat";
import { fromDateOnly, toDateOnly } from "helpers/beneficiaryDocuments";
import { Body, BodyHighlight } from "helpers/TypographyComponents";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTermAssociatedToBeneficiaries } from "store/beneficiaryStore";
import { useAppDispatch } from "store/store";
import { renewTerm } from "store/termStore";
import { fetchTermAssociatedToTitle } from "store/titleStore";
import { fetchWorkTerms } from "store/workStore";

import styles from "./style.module.scss";

interface IRenewTermModal {
    open: boolean;
    onClose: () => void;
    /**
     * Current term id
     */
    termId: number;
    /**
     * Related resource id (title / beneficiar)
     */
    relatedResourceId: number;
    /**
     * Related resource type
     */
    relatedResource: "title" | "beneficiary" | "work";
    /**
     * Start of the term's active period (UTC midnight business date): the backend rejects an earlier end date
     */
    activePeriodStart?: Date | string | null;
}

// Business dates are UTC midnight; the picker works on local days, so rebuild the same calendar day locally.
const getMinEndDate = (activePeriodStart: IRenewTermModal["activePeriodStart"]) => {
    if (!activePeriodStart) return undefined;
    return fromDateOnly(toUtcDay(activePeriodStart)) ?? undefined;
};

const RenewTermModal = ({
    open,
    onClose,
    termId,
    relatedResourceId,
    relatedResource,
    activePeriodStart,
}: IRenewTermModal) => {
    const [date, setDate] = useState<Date | null>(null);
    const [hasDateError, setHasDateError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [t] = useTranslation();
    const minEndDate = getMinEndDate(activePeriodStart);
    const dispatch = useAppDispatch();

    // The modal stays mounted between terms, so clear the previous selection on close.
    const resetAndClose = () => {
        setDate(null);
        setHasDateError(false);
        onClose();
    };

    // Closing mid-request would let the late response reset and close the modal reopened for another term.
    const handleClose = () => {
        if (!isLoading) resetAndClose();
    };

    const handleSubmit = async () => {
        const endDay = toDateOnly(date);
        if (!endDay || hasDateError) return;
        setIsLoading(true);
        // The picker returns local midnight; toISOString would shift it to the previous day for positive offsets
        // (e.g. Europe/Paris), so send the picked calendar day as UTC midnight.
        const endDate = `${endDay}T00:00:00.000Z`;
        try {
            const response = await dispatch(renewTerm({ termId, endDate }));
            if (response.meta.requestStatus === "fulfilled") {
                switch (relatedResource) {
                    case "title":
                        await dispatch(fetchTermAssociatedToTitle({ titleId: relatedResourceId }));
                        break;
                    case "work":
                        await dispatch(fetchWorkTerms(relatedResourceId));
                        break;
                    case "beneficiary":
                        await dispatch(fetchTermAssociatedToBeneficiaries({ beneficiaryId: relatedResourceId }));
                }
                resetAndClose();
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalBase modalTitle={t("Terms.RenewTerm")} open={open} onClose={handleClose}>
            <div className={styles["renew-term-modal"]}>
                <div className={styles["description"]}>
                    <Body>{t("Terms.TermExpiredDescription_1")}</Body>
                    <Body>{t("Terms.TermExpiredDescription_2")}</Body>
                </div>
                <Body color="info">{t("Terms.EndDateInfo")}</Body>
                <div>
                    <BodyHighlight>{t("Terms.TermEndDate")}</BodyHighlight>
                    <StyledDatePicker
                        value={date}
                        onChange={(newDate) => setDate(newDate)}
                        onError={(reason) => setHasDateError(!!reason)}
                        inputFormat="dd/MM/yyyy"
                        // Count the 30 years from the current end when it is already far ahead, so max stays above min.
                        maxYear={Math.max(new Date().getFullYear(), minEndDate?.getFullYear() ?? 0)}
                        maxYearDistance={30}
                        minDate={minEndDate}
                        renderInput={(params) => <StyledTextField fullWidth {...params} />}
                    />
                </div>
                <div className={styles["actions"]}>
                    <StyledButton buttonType="secondary" onClick={handleClose} fullWidth disabled={isLoading}>
                        {t("Terms.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={handleSubmit}
                        fullWidth
                        disabled={!date || hasDateError}
                        loading={isLoading}
                    >
                        {t("Terms.RenewTerm")}
                    </StyledButton>
                </div>
            </div>
        </ModalBase>
    );
};
export default RenewTermModal;
